/**
 * Versões da plataforma de uma BU.
 *
 * Two things happen here, and they are deliberately separate steps: a version is **registered**
 * (push), and later it is **made current**. The BFF creates every version with `isCurrent: false`,
 * because registering a version in the panel is not the same event as that version reaching
 * production — so the form says so instead of silently switching the BU over.
 *
 * The current version is what the dashboard reports and what comes pre-selected when someone
 * writes a What's New, which is why it gets its own row treatment.
 *
 * @module Pages/BUs/BUManager
 */

import { css } from '@emotion/css';
import { UTButton, UTLoading, UTModal, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import {
  createTenantVersion,
  getTenantVersions,
  purgeTenantVersion,
  removeTenantVersion,
  restoreTenantVersion,
  setCurrentTenantVersion,
} from '../../Services';
import {
  formatReleaseDate,
  isVersionFormValid,
  validateVersionForm,
} from '../../Utils/versionForm';
import type { PlatformVersion, Tenant } from '../BUPlatformList/BUPlatformList.types';
import { useActionToast } from '@/Hooks/useActionToast';
import { EmptyState } from '@/Shared/components/EmptyState/EmptyState';
import { IconButton } from '@/Shared/components/IconButton/IconButton';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  width: 100%;
  min-width: 0;
`;

const sectionTitleStyle = css`
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

/*
  Capped and scrollable: a BU accumulates versions for as long as it ships, and the push form below
  is what people come here for. Without a cap the form gets pushed off the bottom of the modal.
*/
const listStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
  max-height: 264px;
  overflow-y: auto;
  /* Room for the scrollbar so it never sits on top of the row's own buttons. */
  padding-right: 4px;
`;

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  min-width: 0;
`;

const currentRowStyle = css`
  border-color: var(--color-success);
  background: var(--color-success-subtle);
`;

/* Listed rather than hidden: invisible is what made a 409 on 2.33 unexplainable. */
const inactiveRowStyle = css`
  border-style: dashed;
  opacity: 0.65;
`;

const versionStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
`;

const nameStyle = css`
  font-size: var(--font-size-sm);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const dateStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  white-space: nowrap;
`;

const spacerStyle = css`
  flex: 1;
`;

const confirmTextStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-danger);
  white-space: nowrap;
`;

const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding: var(--space-ml);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
`;

const hintStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const noteStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const errorStyle = css`
  font-size: var(--font-size-sm);
  color: var(--color-danger);
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-sm);
`;

const EMPTY_FORM = { version: '', name: '', releasedAt: '' };

export interface BUVersionsModalProps {
  open: boolean;
  tenant: Tenant;
  onClose: () => void;
}

export const BUVersionsModal = ({ open, tenant, onClose }: BUVersionsModalProps): JSX.Element => {
  const queryClient = useQueryClient();
  const toast = useActionToast();

  const [form, setForm] = useState(EMPTY_FORM);
  const [showErrors, setShowErrors] = useState(false);
  /** The version whose row is showing the delete confirmation, if any. */
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  const { data: versions, isLoading } = useQuery({
    queryKey: ['tenant-versions', tenant.slug],
    /*
      Inactive ones included on purpose. They are hidden by default on the BFF, but the unique key
      is (tenant, version) regardless of state — so a disabled version still blocks its own number,
      and pushing it again answers 409 for a version nobody can see.
    */
    queryFn: () => getTenantVersions(tenant.slug, { includeInactive: true }),
    enabled: open,
  });

  const rows = useMemo(() => versions ?? [], [versions]);
  const current = rows.find(entry => entry.isCurrent);
  const inactiveCount = rows.filter(entry => !entry.isActive).length;

  const errors = validateVersionForm(form, rows);
  const canSubmit = isVersionFormValid(errors);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['tenant-versions', tenant.slug] });
    // The current version lives on the tenant too, so the card behind the modal must catch up.
    void queryClient.invalidateQueries({ queryKey: ['tenants'] });
  };

  const pushVersion = useMutation({
    mutationFn: () =>
      createTenantVersion(tenant.slug, {
        version: form.version.trim(),
        ...(form.name.trim() ? { name: form.name.trim() } : {}),
        ...(form.releasedAt.trim() ? { releasedAt: form.releasedAt.trim() } : {}),
      }),
    onSuccess: () => {
      invalidate();
      toast.success(
        `Versão ${form.version.trim()} registrada`,
        'Ela ainda não é a ativa — use Tornar ativa quando subir em produção.',
      );
      setForm(EMPTY_FORM);
      setShowErrors(false);
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível registrar a versão', error);
    },
  });

  const makeCurrent = useMutation({
    mutationFn: (version: PlatformVersion) => setCurrentTenantVersion(tenant.slug, version.id),
    onSuccess: (_result, version) => {
      invalidate();
      toast.success(`${tenant.slug} agora está na versão ${version.version}`);
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível trocar a versão ativa', error);
    },
  });

  const restoreVersion = useMutation({
    mutationFn: (version: PlatformVersion) => restoreTenantVersion(tenant.slug, version.id),
    onSuccess: (_result, version) => {
      invalidate();
      toast.success(
        `Versão ${version.version} reativada`,
        'Ela volta para a lista sem virar a ativa — use Tornar ativa para isso.',
      );
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível reativar a versão', error);
    },
  });

  const purgeVersion = useMutation({
    mutationFn: (version: PlatformVersion) => purgeTenantVersion(tenant.slug, version.id),
    onSuccess: (_result, version) => {
      invalidate();
      toast.success(`Versão ${version.version} excluída`, 'O número está livre para novo push.');
      setConfirmingDelete(null);
    },
    onError: (error: Error) => {
      /*
        The BFF's own message is the useful one: it says whether the version is the current one or
        how many announcements still point at it. Rewriting it here would lose the count.
      */
      toast.failure('Não foi possível excluir a versão', error);
      setConfirmingDelete(null);
    },
  });

  const disableVersion = useMutation({
    mutationFn: (version: PlatformVersion) => removeTenantVersion(tenant.slug, version.id),
    onSuccess: (_result, version) => {
      invalidate();
      toast.success(`Versão ${version.version} desativada`);
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível desativar a versão', error);
    },
  });

  const actionError =
    pushVersion.error?.message ??
    makeCurrent.error?.message ??
    disableVersion.error?.message ??
    null;

  const content = (
    <div className={bodyStyle}>
      <span className={sectionTitleStyle}>Versões da plataforma</span>

      {isLoading ? (
        <UTLoading type='dots' />
      ) : rows.length === 0 ? (
        <EmptyState
          id='bu-versions-empty'
          icon='Misc.Tag'
          title='Nenhuma versão registrada'
          description='Registre a primeira versão abaixo para poder marcá-la como ativa.'
        />
      ) : (
        <div className={listStyle}>
          {rows.map(version => (
            <div
              key={version.id}
              id={`bu-version-${version.id}`}
              className={`${rowStyle} ${version.isCurrent ? currentRowStyle : ''} ${
                version.isActive ? '' : inactiveRowStyle
              }`}
            >
              <span className={versionStyle}>{version.version}</span>
              <span className={nameStyle}>{version.name ?? 'sem nome'}</span>
              <span className={dateStyle}>{formatReleaseDate(version.releasedAt)}</span>
              <div className={spacerStyle} />

              {confirmingDelete === version.id ? (
                /*
                  Confirmed in the row rather than in a modal on top of a modal. The action is
                  irreversible, but it is also narrow: the BFF only allows it when nothing points
                  at the version, so the risk is a misclick, not a wrong judgement.
                */
                <>
                  <span className={confirmTextStyle}>Excluir para sempre?</span>
                  <UTButton
                    fitContent
                    variant='tertiary'
                    size='small'
                    text='Cancelar'
                    onClick={() => {
                      setConfirmingDelete(null);
                    }}
                  />
                  <UTButton
                    fitContent
                    id={`bu-version-purge-confirm-${version.id}`}
                    size='small'
                    text={purgeVersion.isPending ? 'Excluindo...' : 'Excluir'}
                    disabled={purgeVersion.isPending}
                    onClick={() => {
                      purgeVersion.mutate(version);
                    }}
                  />
                </>
              ) : version.isCurrent ? (
                <StatusTag status='success' size='sm'>
                  Ativa
                </StatusTag>
              ) : !version.isActive ? (
                <>
                  <StatusTag status='neutral' size='sm'>
                    Desativada
                  </StatusTag>
                  <UTButton
                    fitContent
                    id={`bu-version-restore-${version.id}`}
                    variant='secondary'
                    size='small'
                    text='Reativar'
                    icon='Arrows.ArrowCounterClockwise'
                    disabled={restoreVersion.isPending}
                    onClick={() => {
                      restoreVersion.mutate(version);
                    }}
                  />
                </>
              ) : (
                <>
                  <UTButton
                    fitContent
                    id={`bu-version-activate-${version.id}`}
                    variant='secondary'
                    size='small'
                    text='Tornar ativa'
                    disabled={makeCurrent.isPending}
                    onClick={() => {
                      makeCurrent.mutate(version);
                    }}
                  />
                  <UTButton
                    fitContent
                    id={`bu-version-disable-${version.id}`}
                    variant='tertiary'
                    size='small'
                    text='Desativar'
                    disabled={disableVersion.isPending}
                    onClick={() => {
                      disableVersion.mutate(version);
                    }}
                  />
                </>
              )}

              {/* The current version has no delete: the BFF refuses it, so offering it would lie. */}
              {!version.isCurrent && confirmingDelete !== version.id ? (
                <IconButton
                  id={`bu-version-purge-${version.id}`}
                  icon='Misc.Trash'
                  size='sm'
                  aria-label={`Excluir a versão ${version.version} para sempre`}
                  onClick={() => {
                    setConfirmingDelete(version.id);
                  }}
                />
              ) : null}
            </div>
          ))}
        </div>
      )}

      <div className={formStyle}>
        <UTTextField
          id='bu-version-number'
          topLabel='Versão'
          value={form.version}
          placeholder='Ex.: 2.7'
          state={showErrors && errors.version !== undefined ? 'error' : 'normal'}
          bottomLabel={
            showErrors && errors.version !== undefined
              ? errors.version
              : 'Formato major.minor, ex.: 1.0 ou 2.7.'
          }
          onChange={event => {
            setForm(current_ => ({ ...current_, version: event.target.value }));
          }}
        />

        <UTTextField
          id='bu-version-name'
          topLabel='Nome de exibição (opcional)'
          value={form.name}
          placeholder='Ex.: Winter Release'
          onChange={event => {
            setForm(current_ => ({ ...current_, name: event.target.value }));
          }}
        />

        <UTTextField
          id='bu-version-date'
          topLabel='Data de lançamento (opcional)'
          value={form.releasedAt}
          placeholder='AAAA-MM-DD'
          state={showErrors && errors.releasedAt !== undefined ? 'error' : 'normal'}
          bottomLabel={showErrors && errors.releasedAt !== undefined ? errors.releasedAt : ''}
          onChange={event => {
            setForm(current_ => ({ ...current_, releasedAt: event.target.value }));
          }}
        />

        <span className={hintStyle}>
          A versão entra registrada, mas não ativa. Use <strong>Tornar ativa</strong> na lista
          quando ela subir em produção.
        </span>

        <div className={footerStyle}>
          <UTButton
            fitContent
            variant='secondary'
            text='Limpar'
            disabled={pushVersion.isPending}
            onClick={() => {
              setForm(EMPTY_FORM);
              setShowErrors(false);
            }}
          />
          <UTButton
            fitContent
            id='bu-version-push'
            text={pushVersion.isPending ? 'Enviando...' : 'Push'}
            icon='Arrows.CloudArrowUp'
            disabled={pushVersion.isPending}
            onClick={() => {
              setShowErrors(true);
              if (canSubmit) pushVersion.mutate();
            }}
          />
        </div>
      </div>

      {actionError !== null ? <span className={errorStyle}>{actionError}</span> : null}

      <span className={noteStyle}>
        A versão ativa é a que o dashboard reporta e a que aparece pré-selecionada ao criar um
        What&apos;s New. Hoje: <strong>{current?.version ?? tenant.version}</strong>.
        {inactiveCount > 0
          ? ` ${inactiveCount} desativada(s) aparecem em cinza: continuam ocupando o número e os anúncios presos a elas não são entregues. Reativar devolve as duas coisas.`
          : ' Desativar uma versão não libera o número dela, e silencia os anúncios presos a ela — mas dá para reativar.'}{' '}
        A lixeira apaga de vez e libera o número, e só funciona em versão que não é a ativa e não
        tem nenhum anúncio apontando para ela.
      </span>
    </div>
  );

  return (
    <UTModal
      open={open}
      onClose={onClose}
      title={`Versões · ${tenant.name || tenant.slug}`}
      content={content}
      maxWidth='620px'
      hasButtonClose
      position='center'
    />
  );
};
